import React, { useMemo } from 'react';
import { useContent } from '../../context/ContentContext';
import { resolveMediaUrl } from '../../utils/api';
import { formatRupiah } from '../../utils/formatters';
import { Sparkles, Calendar, Tag, ChevronRight } from 'lucide-react';
import { isSpecialPromoActive } from './PromoCatalogPage';

interface SpecialForYouSectionProps {
  onSelectPromo?: (promoId: string) => void;
}

const DEFAULT_PROMO_IMAGE =
  'https://images.unsplash.com/photo-1514432324607-a09d9b4aefdd?auto=format&fit=crop&w=1000&q=80';

export const SpecialForYouSection: React.FC<SpecialForYouSectionProps> = ({
  onSelectPromo,
}) => {
  const { data } = useContent();

  const activeItems = useMemo(() => {
    return (Array.isArray(data.specialForYou) ? data.specialForYou : [])
      .filter((item) => isSpecialPromoActive(item))
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
  }, [data.specialForYou]);

  if (activeItems.length === 0) return null;

  const handlePromoClick = (promoId: string) => {
    if (onSelectPromo) {
      onSelectPromo(promoId);
    }
    try {
      window.location.hash = `#promo?id=${encodeURIComponent(promoId)}`;
    } catch {}
  };

  return (
    <section id="promo-untukmu" className="py-8 sm:py-12 px-4 lg:px-0 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-5 px-1 sm:px-2">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-[#0056D2]">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <h2 className="font-display font-black text-xl sm:text-2xl text-[#0A192F] uppercase tracking-tight">
                PROMO UNTUKMU
              </h2>
              <p className="text-xs text-slate-500 font-medium">
                Penawaran & diskon spesial untuk pelanggan setia
              </p>
            </div>
          </div>
          <span className="text-[11px] font-bold text-[#0056D2] hidden sm:inline-block">
            {activeItems.length} Promo Aktif
          </span>
        </div>

        {/* Interactive Promo Cards Slider */}
        <div className="flex overflow-x-auto lg:grid lg:grid-cols-3 gap-4 lg:gap-6 px-1 sm:px-2 lg:px-0 pb-4 hide-scrollbar snap-x snap-mandatory">
          {activeItems.map((item) => {
            const bannerUrl = resolveMediaUrl(item.image_url) || DEFAULT_PROMO_IMAGE;
            const hasPeriod = Boolean(item.start_date || item.end_date);
            const periodText = item.start_date && item.end_date
              ? `${item.start_date} - ${item.end_date}`
              : item.end_date
              ? `s.d. ${item.end_date}`
              : item.start_date
              ? `Mulai ${item.start_date}`
              : null;

            return (
              <div
                key={item.id}
                role="button"
                tabIndex={0}
                onClick={() => handlePromoClick(item.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handlePromoClick(item.id);
                  }
                }}
                aria-label={`Lihat detail promo: ${item.title}`}
                className="group bg-[#F8FAFC] rounded-3xl p-4 border border-[#E2E8F0] shadow-xs hover:border-[#0056D2]/40 hover:shadow-lg hover:-translate-y-1 active:scale-[0.98] transition-all duration-200 flex flex-col shrink-0 w-[82%] sm:w-[350px] lg:w-full snap-start cursor-pointer select-none text-left"
              >
                {/* Banner Thumbnail with Badge */}
                <div className="aspect-video w-full rounded-2xl overflow-hidden mb-3.5 bg-slate-200 relative">
                  <img
                    src={bannerUrl}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      const target = e.currentTarget;
                      if (target.src !== DEFAULT_PROMO_IMAGE) {
                        target.src = DEFAULT_PROMO_IMAGE;
                      }
                    }}
                  />
                  {/* Badge */}
                  <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5">
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-[#0056D2] text-white shadow-sm backdrop-blur-xs tracking-wider uppercase">
                      {item.badge_text || 'PROMO'}
                    </span>
                  </div>
                </div>

                {/* Promo Title */}
                <h3 className="font-bold text-base sm:text-lg text-[#0A192F] group-hover:text-[#0056D2] transition-colors truncate mb-1">
                  {item.title}
                </h3>

                {/* Short Description */}
                <p className="text-xs sm:text-sm text-slate-600 mb-3 flex-1 line-clamp-2 leading-relaxed">
                  {item.description}
                </p>

                {/* Period Badge if available */}
                {hasPeriod && periodText && (
                  <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-2.5">
                    <Calendar className="w-3.5 h-3.5 text-[#0056D2] shrink-0" />
                    <span className="truncate">{periodText}</span>
                  </div>
                )}

                {/* Discount Info or Pricing Row */}
                <div className="pt-2.5 border-t border-slate-200/80 flex items-center justify-between">
                  <div>
                    {item.discount_info ? (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-[#0056D2] bg-blue-50 px-2 py-0.5 rounded-md border border-blue-100">
                        <Tag className="w-3 h-3" />
                        <span className="truncate max-w-[170px] sm:max-w-[200px]">
                          {item.discount_info}
                        </span>
                      </span>
                    ) : item.price > 0 ? (
                      <span className="text-sm font-bold text-[#0056D2]">
                        {formatRupiah(item.price)}
                      </span>
                    ) : (
                      <span className="text-xs font-bold text-[#0056D2]">
                        Spesial Promo
                      </span>
                    )}
                  </div>

                  {/* CTA Text with Arrow */}
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-[#0056D2] group-hover:translate-x-0.5 transition-transform">
                    <span>Lihat Detail</span>
                    <ChevronRight className="w-3.5 h-3.5 stroke-[2.5]" />
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
